import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';

const Eventos = () => {
  const { user, API } = useContext(AuthContext);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [createForm, setCreateForm] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'presencial',
    categoria: 'estudiantes',  // Nueva categoría
    fecha_inicio: '',
    fecha_fin: '',
    ubicacion: '',
    link_zoom: '',
    zoom_join_url: '',
    zoom_start_url: '',
    youtube_url: '',
    mostrar_en_landing: false,
    es_publico: false,
    imagenes: [],
    imagen_url: '',  // Link de Imgur
    participantes_maximos: '',
    color_calendario: 'blue'
  });
  const [editForm, setEditForm] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Estado para modal de QR de asistencia presencial
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedEventoQR, setSelectedEventoQR] = useState(null);
  const [creatingZoom, setCreatingZoom] = useState(false);

  // Función para crear reunión de Zoom para evento
  const crearZoomParaEvento = async (isEdit = false) => {
    const form = isEdit ? editForm : createForm;
    const setForm = isEdit ? setEditForm : setCreateForm;
    
    if (!form.titulo || !form.fecha_inicio) {
      toast.error('Completa el título y fecha del evento primero');
      return;
    }
    
    try {
      setCreatingZoom(true);
      toast.info('Creando reunión de Zoom...');
      
      // Llamar al endpoint para crear reunión de Zoom para evento
      const response = await axios.post(`${API}/admin/eventos/crear-zoom`, {
        titulo: form.titulo,
        fecha_inicio: form.fecha_inicio,
        duracion: 120 // 2 horas por defecto para eventos
      });
      
      if (response.data.success) {
        // Guardar ambos links: join para asistentes, start para admin
        setForm({ 
          ...form, 
          link_zoom: response.data.join_url,
          zoom_join_url: response.data.join_url,
          zoom_start_url: response.data.start_url
        });
        toast.success('✅ Reunión de Zoom creada');
      }
    } catch (error) {
      console.error('Error creando Zoom:', error);
      toast.error(error.response?.data?.detail || 'Error al crear reunión de Zoom');
    } finally {
      setCreatingZoom(false);
    }
  };

  useEffect(() => {
    loadEventos();
  }, []);

  const loadEventos = async () => {
    try {
      const response = await axios.get(`${API}/eventos`);
      setEventos(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error(formatBackendError(error) || 'Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir modal de QR de asistencia presencial
  const abrirModalQR = (evento) => {
    setSelectedEventoQR(evento);
    setShowQRModal(true);
  };


  const handleCreateEvento = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!createForm.titulo || !createForm.descripcion || !createForm.fecha_inicio || !createForm.fecha_fin) {
      toast.error('Todos los campos obligatorios deben completarse');
      return;
    }

    if (new Date(createForm.fecha_fin) <= new Date(createForm.fecha_inicio)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    if (createForm.tipo === 'online' && !createForm.link_zoom) {
      toast.error('Los eventos online requieren un link de Zoom');
      return;
    }

    if (createForm.tipo === 'presencial' && !createForm.ubicacion) {
      toast.error('Los eventos presenciales requieren una ubicación');
      return;
    }

    try {
      // Convertir las fechas ingresadas a formato con timezone de Montevideo
      const fechaInicioMontevideo = `${createForm.fecha_inicio}:00-03:00`;
      const fechaFinMontevideo = `${createForm.fecha_fin}:00-03:00`;
      
      const eventoData = {
        ...createForm,
        participantes_maximos: createForm.participantes_maximos ? parseInt(createForm.participantes_maximos) : null,
        fecha_inicio: fechaInicioMontevideo,
        fecha_fin: fechaFinMontevideo
      };

      await axios.post(`${API}/eventos`, eventoData);
      toast.success('Evento creado correctamente');
      loadEventos();
      setIsCreateDialogOpen(false);
      resetCreateForm();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(formatBackendError(error) || 'Error al crear evento');
    }
  };

  const handleEditEvento = async (e) => {
    e.preventDefault();
    
    try {
      // Convertir las fechas ingresadas a formato con timezone de Montevideo
      const fechaInicioMontevideo = editForm.fecha_inicio ? `${editForm.fecha_inicio}:00-03:00` : undefined;
      const fechaFinMontevideo = editForm.fecha_fin ? `${editForm.fecha_fin}:00-03:00` : undefined;
      
      const eventoData = {
        ...editForm,
        participantes_maximos: editForm.participantes_maximos ? parseInt(editForm.participantes_maximos) : null,
        fecha_inicio: fechaInicioMontevideo,
        fecha_fin: fechaFinMontevideo
      };

      // Remove undefined values
      Object.keys(eventoData).forEach(key => {
        if (eventoData[key] === undefined) {
          delete eventoData[key];
        }
      });

      await axios.put(`${API}/eventos/${editingEvento.id}`, eventoData);
      toast.success('Evento actualizado correctamente');
      loadEventos();
      setIsEditDialogOpen(false);
      setEditingEvento(null);
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(formatBackendError(error) || 'Error al actualizar evento');
    }
  };

  const handleCancelarEvento = async (eventoId) => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar este evento?')) {
      return;
    }

    try {
      await axios.put(`${API}/eventos/${eventoId}/cancelar`);
      toast.success('Evento cancelado correctamente');
      loadEventos();
    } catch (error) {
      console.error('Error canceling event:', error);
      toast.error(formatBackendError(error) || 'Error al cancelar evento');
    }
  };

  const handleDeleteEvento = async (eventoId) => {
    console.log('Delete button clicked for event ID:', eventoId);
    
    if (!eventoId) {
      console.error('No event ID provided');
      toast.error('Error: ID de evento no válido');
      return;
    }

    const confirmed = window.confirm('¿Estás seguro de que quieres eliminar este evento permanentemente?');
    console.log('User confirmed deletion:', confirmed);
    
    if (!confirmed) {
      return;
    }

    try {
      console.log('Attempting to delete event:', eventoId);
      const response = await axios.delete(`${API}/eventos/${eventoId}`);
      console.log('Delete response:', response);
      toast.success('Evento eliminado correctamente');
      loadEventos();
    } catch (error) {
      console.error('Error deleting event:', error);
      console.error('Error details:', error.response?.data);
      toast.error(formatBackendError(error) || 'Error al eliminar evento');
    }
  };

  const openEditDialog = (evento) => {
    setEditingEvento(evento);
    setEditForm({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      tipo: evento.tipo,
      categoria: evento.categoria || 'estudiantes',
      fecha_inicio: evento.fecha_inicio ? convertUTCToMontevideo(evento.fecha_inicio) : '',
      fecha_fin: evento.fecha_fin ? convertUTCToMontevideo(evento.fecha_fin) : '',
      ubicacion: evento.ubicacion || '',
      link_zoom: evento.link_zoom || '',
      zoom_join_url: evento.zoom_join_url || '',
      zoom_start_url: evento.zoom_start_url || '',
      youtube_url: evento.youtube_url || '',
      mostrar_en_landing: evento.mostrar_en_landing || false,
      es_publico: evento.es_publico || false,
      imagenes: evento.imagenes || [],
      imagen_url: evento.imagen_url || '',
      participantes_maximos: evento.participantes_maximos || '',
      color_calendario: evento.color_calendario || 'blue'
    });
    setIsEditDialogOpen(true);
  };

  const resetCreateForm = () => {
    setCreateForm({
      titulo: '',
      descripcion: '',
      tipo: 'presencial',
      categoria: 'estudiantes',
      fecha_inicio: '',
      fecha_fin: '',
      ubicacion: '',
      link_zoom: '',
      zoom_join_url: '',
      zoom_start_url: '',
      youtube_url: '',
      mostrar_en_landing: false,
      es_publico: false,
      imagenes: [],
      imagen_url: '',
      participantes_maximos: '',
      color_calendario: 'blue'
    });
  };

  // Función para convertir fecha UTC a hora local de Montevideo
  const convertUTCToMontevideo = (utcDateString) => {
    if (!utcDateString) return '';
    
    try {
      // Si la fecha NO tiene Z al final, agregarla para que se interprete como UTC
      let dateStr = utcDateString;
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr = dateStr + 'Z';
      }
      
      // Parsear la fecha UTC que viene del backend
      const utcDate = new Date(dateStr);
      
      // Convertir a hora de Montevideo (UTC-3)
      // Restar 3 horas del tiempo UTC
      const montevideoTime = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      
      // Formatear manualmente para evitar conversión automática del navegador
      // Usamos getUTC* porque ya ajustamos el tiempo manualmente
      const year = montevideoTime.getUTCFullYear();
      const month = String(montevideoTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(montevideoTime.getUTCDate()).padStart(2, '0');
      const hours = String(montevideoTime.getUTCHours()).padStart(2, '0');
      const minutes = String(montevideoTime.getUTCMinutes()).padStart(2, '0');
      
      // Retornar en formato que datetime-local espera
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error converting date:', error);
      return '';
    }
  };

  const formatDateTime = (dateTimeString) => {
    try {
      // Convertir UTC a hora de Montevideo para mostrar
      const utcDate = new Date(dateTimeString);
      // Montevideo está UTC-3, RESTAR 3 horas para mostrar hora local
      const montevideoDate = new Date(utcDate.getTime() - (3 * 60 * 60 * 1000));
      return format(montevideoDate, "dd/MM/yyyy 'a las' HH:mm", { locale: es });
    } catch {
      return 'Fecha inválida';
    }
  };

  // Función para formatear errores de validación del backend
  const formatBackendError = (error) => {
    if (!error.response?.data?.detail) {
      return 'Error desconocido';
    }

    const detail = error.response.data.detail;
    
    // Si es un string, devolverlo directamente
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Si es un array de errores de validación de Pydantic
    if (Array.isArray(detail)) {
      const errorMessages = detail.map(err => {
        if (typeof err === 'object' && err.msg) {
          const field = err.loc && err.loc.length > 0 ? err.loc[err.loc.length - 1] : 'campo';
          return `${field}: ${err.msg}`;
        }
        return String(err);
      });
      return errorMessages.join(', ');
    }
    
    // Si es un objeto, convertirlo a string
    if (typeof detail === 'object') {
      if (detail.msg) {
        return detail.msg;
      }
      return JSON.stringify(detail);
    }
    
    return String(detail);
  };

  const getEventTypeIcon = (tipo) => {
    return tipo === 'online' ? 'fas fa-video' : 'fas fa-map-marker-alt';
  };

  const getEventTypeColor = (tipo) => {
    return tipo === 'online' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  };

  const getStatusColor = (estado) => {
    return estado === 'cancelado' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Eventos</h1>
            <p className="text-gray-600">Administra eventos presenciales y online del instituto</p>
          </div>
        </div>
        {user?.rol === 'admin' && (
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="instituto-gradient text-white btn-hover"
          >
            <i className="fas fa-plus mr-2"></i>
            Crear Evento
          </Button>
        )}
      </div>

      {/* Access Control */}
      {user?.rol !== 'admin' && (
        <Card className="shadow-md border-l-4 border-yellow-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-info-circle text-yellow-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Solo Visualización</h3>
                <p className="text-gray-600">Solo los administradores pueden crear, modificar y cancelar eventos.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Eventos List */}
      <div className="grid gap-6">
        {eventos.length > 0 ? (
          eventos.map((evento) => (
            <Card key={evento.id} className="shadow-lg border-0">
              <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <i className={`${getEventTypeIcon(evento.tipo)} text-blue-600 mr-3`}></i>
                    <div>
                      <div className="text-xl">{evento.titulo}</div>
                      <div className="text-sm text-gray-600 font-normal mt-1">
                        {evento.descripcion}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      variant="secondary" 
                      className={getStatusColor(evento.estado)}
                    >
                      {evento.estado === 'cancelado' ? 'Cancelado' : 'Activo'}
                    </Badge>
                    <Badge 
                      variant="secondary" 
                      className={getEventTypeColor(evento.tipo)}
                    >
                      <i className={`${getEventTypeIcon(evento.tipo)} mr-1`}></i>
                      {evento.tipo === 'online' ? 'Online' : 'Presencial'}
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Imagen del evento (si existe) */}
                  {evento.imagen_url && (
                    <div className="mb-4">
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-purple-200 shadow-sm">
                        <img 
                          src={evento.imagen_url} 
                          alt={evento.titulo}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full bg-gray-100"><span class="text-gray-400"><i class="fas fa-image mr-2"></i>Imagen no disponible</span></div>';
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 italic">
                        <i className="fas fa-link mr-1"></i>
                        Imagen desde Imgur
                      </p>
                    </div>
                  )}
                  
                  {/* Fecha y Hora */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <i className="fas fa-calendar-check text-green-600 mr-2"></i>
                          <span className="font-semibold text-gray-900">Inicio</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-green-700">
                            {formatDateTime(evento.fecha_inicio)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <i className="fas fa-calendar-times text-red-600 mr-2"></i>
                          <span className="font-semibold text-gray-900">Fin</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-red-700">
                            {formatDateTime(evento.fecha_fin)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ubicación o Link */}
                  {evento.tipo === 'presencial' && evento.ubicacion && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="fas fa-map-marker-alt text-blue-600 mr-2"></i>
                        <span className="font-semibold text-gray-900 mr-2">Ubicación:</span>
                        <span className="text-blue-700">{evento.ubicacion}</span>
                      </div>
                    </div>
                  )}

                  {evento.tipo === 'online' && (evento.link_zoom || evento.zoom_join_url) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center">
                          <i className="fas fa-video text-green-600 mr-2"></i>
                          <span className="font-semibold text-gray-900">Zoom:</span>
                        </div>
                        <div className="flex gap-2">
                          {/* Botón para Admin: Iniciar Transmisión */}
                          {user?.rol === 'admin' && evento.zoom_start_url && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => window.open(evento.zoom_start_url, '_blank')}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              <i className="fas fa-broadcast-tower mr-2"></i>
                              Transmitir
                            </Button>
                          )}
                          {/* Botón para todos: Unirse */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(evento.zoom_join_url || evento.link_zoom, '_blank')}
                            className="text-green-600 hover:text-green-700"
                          >
                            <i className="fas fa-sign-in-alt mr-2"></i>
                            Unirse
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Imágenes del Evento */}
                  {evento.imagenes && evento.imagenes.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-gray-700">
                        <i className="fas fa-images mr-2"></i>
                        Imágenes
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {evento.imagenes.map((imageUrl, index) => (
                          <img
                            key={index}
                            src={imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`}
                            alt={`Imagen ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => {
                              const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`;
                              window.open(fullUrl, '_blank');
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Información adicional */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <span>
                        <i className="fas fa-user mr-1"></i>
                        Creado por: {evento.creador_nombre}
                      </span>
                      {evento.participantes_maximos && (
                        <span>
                          <i className="fas fa-users mr-1"></i>
                          Máx: {evento.participantes_maximos} personas
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Código QR para asistencia presencial - Siempre visible */}
                  {evento.qr_code_data && (
                    <div 
                      className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => abrirModalQR(evento)}
                      title="Haz clic para ver el QR en tamaño completo"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="bg-white p-2 rounded border border-purple-300">
                            <QRCodeSVG 
                              value={`${window.location.origin}/marcar-asistencia-evento-qr?evento_id=${evento.id}`}
                              size={80}
                              level="H"
                              includeMargin={false}
                            />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-purple-800 mb-1">
                            <i className="fas fa-qrcode mr-1"></i>
                            QR Asistencia Presencial
                          </p>
                          <p className="text-xs text-purple-600 mb-1">Para registro de asistencia al evento</p>
                          <p className="text-xs text-purple-500 italic">
                            <i className="fas fa-hand-pointer mr-1"></i>
                            Toca para expandir
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botones de acción (solo admin) */}
                  {user?.rol === 'admin' && (
                    <div className="flex space-x-2 pt-2">
                      <Button
                        onClick={() => openEditDialog(evento)}
                        variant="outline"
                        size="sm"
                        className="texto-blue-600 hover:text-blue-700 flex-1"
                      >
                        <i className="fas fa-edit mr-2"></i>
                        Editar
                      </Button>
                      {evento.estado === 'activo' && (
                        <Button
                          onClick={() => handleCancelarEvento(evento.id)}
                          variant="outline"
                          size="sm"
                          className="text-yellow-600 hover:text-yellow-700 flex-1"
                        >
                          <i className="fas fa-ban mr-2"></i>
                          Cancelar
                        </Button>
                      )}
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Delete button clicked for evento:', evento);
                          handleDeleteEvento(evento.id);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 flex-1"
                      >
                        <i className="fas fa-trash mr-2"></i>
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="shadow-md border-0">
            <CardContent className="p-12 text-center">
              <i className="fas fa-calendar-plus text-gray-300 text-6xl mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay eventos</h3>
              <p className="text-gray-600 mb-4">
                {user?.rol === 'admin' 
                  ? 'Aún no se han creado eventos. Crea el primer evento del instituto.'
                  : 'Aún no hay eventos programados.'
                }
              </p>
              {user?.rol === 'admin' && (
                <Button onClick={() => setIsCreateDialogOpen(true)} className="instituto-gradient text-white">
                  <i className="fas fa-plus mr-2"></i>
                  Crear Primer Evento
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Evento Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Evento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvento} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-titulo">Título del Evento *</Label>
                <Input
                  id="create-titulo"
                  value={createForm.titulo}
                  onChange={(e) => setCreateForm({ ...createForm, titulo: e.target.value })}
                  placeholder="Ej: Conferencia Bíblica"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="create-tipo">Tipo de Evento *</Label>
                <Select value={createForm.tipo} onValueChange={(value) => setCreateForm({ ...createForm, tipo: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">
                      <div className="flex items-center">
                        <i className="fas fa-map-marker-alt mr-2"></i>
                        Presencial
                      </div>
                    </SelectItem>
                    <SelectItem value="online">
                      <div className="flex items-center">
                        <i className="fas fa-video mr-2"></i>
                        Online
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Nuevo selector de categoría */}
              <div>
                <Label htmlFor="create-categoria">Categoría *</Label>
                <Select value={createForm.categoria} onValueChange={(value) => setCreateForm({ ...createForm, categoria: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="¿Quién puede ver este evento?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publica">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-globe text-green-600"></i>
                        <span>Pública - Visible sin login</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="estudiantes">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-user-graduate text-blue-600"></i>
                        <span>Estudiantes y Profesores</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="profesores">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chalkboard-teacher text-purple-600"></i>
                        <span>Solo Profesores</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {createForm.categoria === 'publica' 
                    ? '🌍 Visible en la landing page para visitantes sin login' 
                    : createForm.categoria === 'estudiantes' 
                      ? '👥 Visible para estudiantes y profesores' 
                      : '👨‍🏫 Solo visible para profesores'}
                </p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="create-descripcion">Descripción *</Label>
              <Textarea
                id="create-descripcion"
                value={createForm.descripcion}
                onChange={(e) => setCreateForm({ ...createForm, descripcion: e.target.value })}
                placeholder="Describe el evento, agenda, objetivos..."
                rows={3}
                required
              />
            </div>
            
            {/* Imagen Principal del Evento (URL Externa) */}
            <div className="space-y-2 border-t pt-4">
              <Label className="text-base font-semibold">
                <i className="fas fa-image mr-2 text-purple-600"></i>
                Imagen Principal (URL de Imgur)
              </Label>
              <Input
                type="url"
                value={createForm.imagen_url || ''}
                onChange={(e) => setCreateForm({ ...createForm, imagen_url: e.target.value })}
                placeholder="https://i.imgur.com/ejemplo.jpg"
              />
              <p className="text-xs text-gray-500">
                <i className="fas fa-info-circle mr-1"></i>
                Esta imagen se mostrará como portada del evento
              </p>
              {/* Preview de imagen principal */}
              {createForm.imagen_url && (
                <div className="mt-2">
                  <img 
                    src={createForm.imagen_url} 
                    alt="Preview"
                    className="w-full max-w-xs h-32 object-cover rounded border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Imágenes Adicionales del Evento */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-base font-semibold">
                <i className="fas fa-images mr-2 text-blue-600"></i>
                Imágenes Adicionales (Opcional)
              </Label>
              
              {/* Preview de imágenes */}
              {createForm.imagenes.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {createForm.imagenes.map((imageUrl, index) => (
                    <div key={index} className="relative">
                      <img
                        src={imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-20 object-cover rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCreateForm(prev => ({
                            ...prev,
                            imagenes: prev.imagenes.filter((_, i) => i !== index)
                          }));
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Opción 1: Subir desde PC */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  <i className="fas fa-upload mr-2"></i>
                  Opción 1: Subir desde PC
                </Label>
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setUploadingImage(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        const response = await axios.post(`${API}/upload/class-banner`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        setCreateForm(prev => ({
                          ...prev,
                          imagenes: [...prev.imagenes, response.data.banner_url]
                        }));
                        toast.success('Imagen subida correctamente');
                      } catch (error) {
                        toast.error('Error al subir imagen');
                      } finally {
                        setUploadingImage(false);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingImage}
                    className="hidden"
                    id="evento-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('evento-image-upload').click()}
                    disabled={uploadingImage}
                    size="sm"
                  >
                    <i className={`fas ${uploadingImage ? 'fa-spinner fa-spin' : 'fa-image'} mr-2`}></i>
                    {uploadingImage ? 'Subiendo...' : 'Agregar Imagen'}
                  </Button>
                  <span className="text-xs text-gray-500">Máx. 5MB</span>
                </div>
              </div>
              
              {/* Divisor */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">O</span>
                </div>
              </div>
              
              {/* Opción 2: URL Externa */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  <i className="fas fa-link mr-2"></i>
                  Opción 2: URL de Imagen Externa (Imgur, etc.)
                </Label>
                <div className="flex space-x-2">
                  <Input
                    type="url"
                    placeholder="https://i.imgur.com/ejemplo.jpg"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                          setCreateForm(prev => ({
                            ...prev,
                            imagenes: [...prev.imagenes, url]
                          }));
                          e.target.value = '';
                          toast.success('URL de imagen agregada');
                        } else {
                          toast.error('Por favor ingresa una URL válida');
                        }
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const input = e.target.closest('div').querySelector('input');
                      const url = input.value.trim();
                      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                        setCreateForm(prev => ({
                          ...prev,
                          imagenes: [...prev.imagenes, url]
                        }));
                        input.value = '';
                        toast.success('URL de imagen agregada');
                      } else {
                        toast.error('Por favor ingresa una URL válida');
                      }
                    }}
                  >
                    <i className="fas fa-plus"></i>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  Pega la URL y presiona Enter o click en +
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-fecha-inicio">Fecha y Hora de Inicio * 🇺🇾</Label>
                <Input
                  id="create-fecha-inicio"
                  type="datetime-local"
                  value={createForm.fecha_inicio}
                  onChange={(e) => setCreateForm({ ...createForm, fecha_inicio: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Hora de Montevideo (UTC-3)</p>
              </div>
              
              <div>
                <Label htmlFor="create-fecha-fin">Fecha y Hora de Fin * 🇺🇾</Label>
                <Input
                  id="create-fecha-fin"
                  type="datetime-local"
                  value={createForm.fecha_fin}
                  onChange={(e) => setCreateForm({ ...createForm, fecha_fin: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Hora de Montevideo (UTC-3)</p>
              </div>
            </div>
            
            {createForm.tipo === 'presencial' && (
              <div>
                <Label htmlFor="create-ubicacion">Ubicación *</Label>
                <Input
                  id="create-ubicacion"
                  value={createForm.ubicacion}
                  onChange={(e) => setCreateForm({ ...createForm, ubicacion: e.target.value })}
                  placeholder="Ej: Auditorio Principal, Salón de Conferencias..."
                  required={createForm.tipo === 'presencial'}
                />
              </div>
            )}
            
            {createForm.tipo === 'online' && (
              <div>
                <Label htmlFor="create-link-zoom">Link de Zoom *</Label>
                <div className="flex gap-2">
                  <Input
                    id="create-link-zoom"
                    value={createForm.link_zoom}
                    onChange={(e) => setCreateForm({ ...createForm, link_zoom: e.target.value })}
                    placeholder="https://zoom.us/j/..."
                    required={createForm.tipo === 'online'}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => crearZoomParaEvento(false)}
                    disabled={creatingZoom || !createForm.titulo || !createForm.fecha_inicio}
                    className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                  >
                    {creatingZoom ? (
                      <><span className="animate-spin mr-1">⏳</span> Creando...</>
                    ) : (
                      <><i className="fas fa-video mr-1"></i> Crear Zoom</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Pega un link existente o usa "Crear Zoom" para generar uno automáticamente
                </p>
              </div>
            )}
            
            {/* Sección YouTube y Landing */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <i className="fab fa-youtube text-red-600"></i>
                Transmisión YouTube (Opcional)
              </h4>
              
              {/* Checkbox Evento Público */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="create-es-publico"
                  checked={createForm.es_publico}
                  onChange={(e) => setCreateForm({ ...createForm, es_publico: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="create-es-publico" className="cursor-pointer">
                  🌍 Evento Público (visible para visitantes)
                </Label>
              </div>
              
              {/* Campo YouTube URL */}
              <div className="mb-3">
                <Label htmlFor="create-youtube-url">Link de YouTube</Label>
                <Input
                  id="create-youtube-url"
                  value={createForm.youtube_url || ''}
                  onChange={(e) => setCreateForm({ ...createForm, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Crea la transmisión en <a href="https://studio.youtube.com" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">YouTube Studio</a> y pega el link aquí
                </p>
              </div>
              
              {/* Checkbox Mostrar en Landing */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create-mostrar-landing"
                  checked={createForm.mostrar_en_landing}
                  onChange={(e) => setCreateForm({ ...createForm, mostrar_en_landing: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                  disabled={!createForm.es_publico}
                />
                <Label htmlFor="create-mostrar-landing" className={`cursor-pointer ${!createForm.es_publico ? 'text-gray-400' : ''}`}>
                  📺 Mostrar en Landing Page (sección "En Vivo")
                </Label>
              </div>
              {!createForm.es_publico && (
                <p className="text-xs text-gray-400 mt-1 ml-6">Marca "Evento Público" primero</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="create-participantes">Participantes Máximos (opcional)</Label>
                <Input
                  id="create-participantes"
                  type="number"
                  min="1"
                  max="500"
                  value={createForm.participantes_maximos}
                  onChange={(e) => setCreateForm({ ...createForm, participantes_maximos: e.target.value })}
                  placeholder="Ej: 100"
                />
              </div>
              
              <div>
                <Label htmlFor="create-color">Color en Calendario</Label>
                <Select value={createForm.color_calendario} onValueChange={(value) => setCreateForm({ ...createForm, color_calendario: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Azul</SelectItem>
                    <SelectItem value="green">Verde</SelectItem>
                    <SelectItem value="purple">Púrpura</SelectItem>
                    <SelectItem value="red">Rojo</SelectItem>
                    <SelectItem value="orange">Naranja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetCreateForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="instituto-gradient text-white">
                Crear Evento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Evento Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Evento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditEvento} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-titulo">Título del Evento</Label>
                <Input
                  id="edit-titulo"
                  value={editForm.titulo || ''}
                  onChange={(e) => setEditForm({ ...editForm, titulo: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-tipo">Tipo de Evento</Label>
                <Select value={editForm.tipo || 'presencial'} onValueChange={(value) => setEditForm({ ...editForm, tipo: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">
                      <div className="flex items-center">
                        <i className="fas fa-map-marker-alt mr-2"></i>
                        Presencial
                      </div>
                    </SelectItem>
                    <SelectItem value="online">
                      <div className="flex items-center">
                        <i className="fas fa-video mr-2"></i>
                        Online
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Selector de categoría en edición */}
              <div>
                <Label htmlFor="edit-categoria">Categoría</Label>
                <Select value={editForm.categoria || 'estudiantes'} onValueChange={(value) => setEditForm({ ...editForm, categoria: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publica">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-globe text-green-600"></i>
                        Pública
                      </div>
                    </SelectItem>
                    <SelectItem value="estudiantes">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-user-graduate text-blue-600"></i>
                        Estudiantes
                      </div>
                    </SelectItem>
                    <SelectItem value="profesores">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chalkboard-teacher text-purple-600"></i>
                        Profesores
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-descripcion">Descripción</Label>
              <Textarea
                id="edit-descripcion"
                value={editForm.descripcion || ''}
                onChange={(e) => setEditForm({ ...editForm, descripcion: e.target.value })}
                rows={3}
              />
            </div>
            
            {/* Imagen Principal del Evento - Edición */}
            <div className="space-y-2 border-t pt-4">
              <Label className="text-base font-semibold">
                <i className="fas fa-image mr-2 text-purple-600"></i>
                Imagen Principal (URL de Imgur)
              </Label>
              <Input
                type="url"
                value={editForm.imagen_url || ''}
                onChange={(e) => setEditForm({ ...editForm, imagen_url: e.target.value })}
                placeholder="https://i.imgur.com/ejemplo.jpg"
              />
              <p className="text-xs text-gray-500">
                <i className="fas fa-info-circle mr-1"></i>
                Esta imagen se mostrará como portada del evento
              </p>
              {/* Preview de imagen principal */}
              {editForm.imagen_url && (
                <div className="mt-2">
                  <img 
                    src={editForm.imagen_url} 
                    alt="Preview"
                    className="w-full max-w-xs h-32 object-cover rounded border"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Imágenes Adicionales del Evento - Edición */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-base font-semibold">
                <i className="fas fa-images mr-2 text-blue-600"></i>
                Imágenes Adicionales (Opcional)
              </Label>
              
              {/* Preview de imágenes existentes */}
              {editForm.imagenes && editForm.imagenes.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {editForm.imagenes.map((imageUrl, index) => (
                    <div key={index} className="relative">
                      <img
                        src={imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-20 object-cover rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setEditForm(prev => ({
                            ...prev,
                            imagenes: prev.imagenes.filter((_, i) => i !== index)
                          }));
                        }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* URL Externa para imagen */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  <i className="fas fa-link mr-2"></i>
                  URL de Imagen Externa (Imgur, etc.)
                </Label>
                <div className="flex space-x-2">
                  <Input
                    type="url"
                    placeholder="https://i.imgur.com/ejemplo.jpg"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                          setEditForm(prev => ({
                            ...prev,
                            imagenes: [...(prev.imagenes || []), url],
                            imagen_url: url
                          }));
                          e.target.value = '';
                          toast.success('URL de imagen agregada');
                        } else {
                          toast.error('Por favor ingresa una URL válida');
                        }
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      const input = e.target.closest('div').querySelector('input');
                      const url = input.value.trim();
                      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                        setEditForm(prev => ({
                          ...prev,
                          imagenes: [...(prev.imagenes || []), url],
                          imagen_url: url
                        }));
                        input.value = '';
                        toast.success('URL de imagen agregada');
                      } else {
                        toast.error('Por favor ingresa una URL válida');
                      }
                    }}
                  >
                    <i className="fas fa-plus"></i>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  Pega la URL y presiona Enter o click en +
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-fecha-inicio">Fecha y Hora de Inicio 🇺🇾</Label>
                <Input
                  id="edit-fecha-inicio"
                  type="datetime-local"
                  value={editForm.fecha_inicio || ''}
                  onChange={(e) => setEditForm({ ...editForm, fecha_inicio: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Hora de Montevideo (UTC-3)</p>
              </div>
              
              <div>
                <Label htmlFor="edit-fecha-fin">Fecha y Hora de Fin 🇺🇾</Label>
                <Input
                  id="edit-fecha-fin"
                  type="datetime-local"
                  value={editForm.fecha_fin || ''}
                  onChange={(e) => setEditForm({ ...editForm, fecha_fin: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Hora de Montevideo (UTC-3)</p>
              </div>
            </div>
            
            {(editForm.tipo === 'presencial' || editingEvento?.tipo === 'presencial') && (
              <div>
                <Label htmlFor="edit-ubicacion">Ubicación</Label>
                <Input
                  id="edit-ubicacion"
                  value={editForm.ubicacion || ''}
                  onChange={(e) => setEditForm({ ...editForm, ubicacion: e.target.value })}
                />
              </div>
            )}
            
            {(editForm.tipo === 'online' || editingEvento?.tipo === 'online') && (
              <div>
                <Label htmlFor="edit-link-zoom">Link de Zoom</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-link-zoom"
                    value={editForm.link_zoom || ''}
                    onChange={(e) => setEditForm({ ...editForm, link_zoom: e.target.value })}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => crearZoomParaEvento(true)}
                    disabled={creatingZoom || !editForm.titulo || !editForm.fecha_inicio}
                    className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                  >
                    {creatingZoom ? (
                      <><span className="animate-spin mr-1">⏳</span> Creando...</>
                    ) : (
                      <><i className="fas fa-video mr-1"></i> Crear Zoom</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Pega un link existente o usa "Crear Zoom" para generar uno
                </p>
              </div>
            )}
            
            {/* Sección YouTube y Landing - Edición */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <i className="fab fa-youtube text-red-600"></i>
                Transmisión YouTube (Opcional)
              </h4>
              
              {/* Checkbox Evento Público */}
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="edit-es-publico"
                  checked={editForm.es_publico}
                  onChange={(e) => setEditForm({ ...editForm, es_publico: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="edit-es-publico" className="cursor-pointer">
                  🌍 Evento Público (visible para visitantes)
                </Label>
              </div>
              
              {/* Campo YouTube URL */}
              <div className="mb-3">
                <Label htmlFor="edit-youtube-url">Link de YouTube</Label>
                <Input
                  id="edit-youtube-url"
                  value={editForm.youtube_url || ''}
                  onChange={(e) => setEditForm({ ...editForm, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Crea la transmisión en <a href="https://studio.youtube.com" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">YouTube Studio</a> y pega el link aquí
                </p>
              </div>
              
              {/* Checkbox Mostrar en Landing */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-mostrar-landing"
                  checked={editForm.mostrar_en_landing}
                  onChange={(e) => setEditForm({ ...editForm, mostrar_en_landing: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                  disabled={!editForm.es_publico}
                />
                <Label htmlFor="edit-mostrar-landing" className={`cursor-pointer ${!editForm.es_publico ? 'text-gray-400' : ''}`}>
                  📺 Mostrar en Landing Page (sección "En Vivo")
                </Label>
              </div>
              {!editForm.es_publico && (
                <p className="text-xs text-gray-400 mt-1 ml-6">Marca "Evento Público" primero</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-participantes">Participantes Máximos</Label>
                <Input
                  id="edit-participantes"
                  type="number"
                  min="1"
                  max="500"
                  value={editForm.participantes_maximos || ''}
                  onChange={(e) => setEditForm({ ...editForm, participantes_maximos: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-color">Color en Calendario</Label>
                <Select value={editForm.color_calendario || 'blue'} onValueChange={(value) => setEditForm({ ...editForm, color_calendario: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Azul</SelectItem>
                    <SelectItem value="green">Verde</SelectItem>
                    <SelectItem value="purple">Púrpura</SelectItem>
                    <SelectItem value="red">Rojo</SelectItem>
                    <SelectItem value="orange">Naranja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingEvento(null);
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="instituto-gradient text-white">
                Actualizar Evento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de QR para Asistencia Presencial a Eventos */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-purple-700 flex items-center justify-center">
              <i className="fas fa-qrcode mr-2 text-2xl"></i>
              Código QR - Asistencia a Evento
            </DialogTitle>
          </DialogHeader>
          
          {selectedEventoQR && (
            <div className="space-y-4">
              {/* Información del evento */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-1 text-center">
                  {selectedEventoQR.titulo}
                </h4>
                <p className="text-sm text-gray-600 text-center">
                  {selectedEventoQR.tipo === 'online' ? 'Evento Online' : 'Evento Presencial'}
                </p>
              </div>

              {/* Instrucciones */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 text-center mb-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  Toca el código QR para expandir en pantalla completa
                </p>
                <p className="text-xs text-blue-700 text-center">
                  💡 El brillo aumentará automáticamente para mejor escaneo
                </p>
              </div>

              {/* Código QR */}
              <div 
                className="flex flex-col items-center justify-center p-6 bg-white rounded-lg border-2 border-purple-300 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={async () => {
                  // Aumentar brillo de pantalla al máximo
                  try {
                    if ('wakeLock' in navigator) {
                      const wakeLock = await navigator.wakeLock.request('screen');
                      console.log('Wake Lock activado - pantalla al máximo brillo');
                    }
                  } catch (err) {
                    console.log('Wake Lock no disponible:', err);
                  }
                  
                  // Abrir QR en pantalla completa en móviles
                  const qrUrl = `${window.location.origin}/marcar-asistencia-evento-qr?evento_id=${selectedEventoQR.id}`;
                  const width = window.innerWidth;
                  
                  if (width < 768) {
                    // En móviles, abrir modal de pantalla completa
                    const qrFullScreen = document.getElementById('qr-evento-fullscreen');
                    if (qrFullScreen) {
                      qrFullScreen.classList.remove('hidden');
                      // Agregar fondo blanco brillante
                      qrFullScreen.style.backgroundColor = '#ffffff';
                    }
                  }
                }}
              >
                <QRCodeSVG 
                  value={`${window.location.origin}/marcar-asistencia-evento-qr?evento_id=${selectedEventoQR.id}`}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Escanea con tu celular para marcar asistencia
                </p>
              </div>

              {/* Información adicional */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-semibold text-purple-800 mb-2 text-sm">
                  <i className="fas fa-lightbulb mr-1"></i>
                  ¿Cómo funciona?
                </h5>
                <ul className="text-xs text-purple-700 space-y-1">
                  <li>• Escanea el QR con tu celular</li>
                  <li>• Debes estar logueado en el sistema</li>
                  <li>• Se marca asistencia automáticamente</li>
                  <li>• Solo válido el día del evento</li>
                  <li>• Marca única (no se puede repetir)</li>
                </ul>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowQRModal(false);
                  setSelectedEventoQR(null);
                }}
              >
                <i className="fas fa-times mr-2"></i>
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de QR en pantalla completa para móviles */}
      <div 
        id="qr-evento-fullscreen" 
        className="hidden fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-4"
        onClick={(e) => {
          if (e.target.id === 'qr-evento-fullscreen') {
            e.target.classList.add('hidden');
          }
        }}
      >
        {selectedEventoQR && (
          <>
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
              onClick={() => document.getElementById('qr-evento-fullscreen').classList.add('hidden')}
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-purple-700 mb-2">
                {selectedEventoQR.titulo}
              </h3>
              <p className="text-gray-600">Asistencia al Evento</p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-2xl">
              <QRCodeSVG 
                value={`${window.location.origin}/marcar-asistencia-evento-qr?evento_id=${selectedEventoQR.id}`}
                size={Math.min(window.innerWidth - 100, 400)}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <p className="text-gray-600 mt-6 text-center">
              Escanea este código para marcar tu asistencia
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Eventos;